import { useEffect, useRef } from 'react'
import { useSettingsStore } from '../stores/settingsStore'
import type { CogRow } from '../types'
import { useDashboardData } from './useDashboardData'
import { useMarketStore } from '../services/store'
import { runInWorker } from '../components/GlobalHistoryFetcher'

// 🚀 PERF FIX: Module-level cache survives component unmounts.
// When you navigate between Inspector and Analysis, we instantly return the cached CoG
// instead of recalculating 320+ snapshots and freezing the browser.
const GLOBAL_COG_CACHE = new Map<string, CogRow[]>()

export function useCogEngine ()
{
    const activeSymbol = useSettingsStore( s => s.symbol ) || ''
    const range = useSettingsStore( s => s.range ) || 10
    const inspectionSource = useSettingsStore( s => s.inspectionSource )

    const historyRef = useRef<any[]>( [] )
    const lastHistoryRef = useRef<any[]>( [] )
    const { baseOptions: resolvedBaseOpts, baseSpot, effectiveExpiry } = useDashboardData()

    const baseOptsRef = useRef<any[]>( [] )
    baseOptsRef.current = resolvedBaseOpts || []
    const baseSpotRef = useRef( 0 )
    baseSpotRef.current = baseSpot

    const baseOptsFingerprint = `${resolvedBaseOpts?.length || 0}-${baseSpot}-${resolvedBaseOpts?.[ 0 ]?.strike || 0}`
    const step = activeSymbol === 'NIFTY' ? 50 : 100

    const history = useMarketStore( ( state ) =>
    {
        if ( inspectionSource === 'HistoricalSnapshot' ) return state.historicalHistory?.[ activeSymbol ]
        if ( inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD' ) return state.rangeHistory?.[ activeSymbol ]
        return state.intradayHistory?.[ activeSymbol ]
    } )

    useEffect( () =>
    {
        historyRef.current = history || []
    }, [ history ] )

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>( null )
    const pollRef = useRef<ReturnType<typeof setInterval> | null>( null )
    // Track in-flight worker calls to prevent duplicates
    const computingRef = useRef( false )

    useEffect( () =>
    {
        const checkAndCompute = () =>
        {
            const h = historyRef.current
            if ( !h || h.length === 0 || !activeSymbol ) return
            if ( h === lastHistoryRef.current ) return
            lastHistoryRef.current = h
            scheduleCompute()
        }

        const scheduleCompute = () =>
        {
            if ( debounceRef.current ) clearTimeout( debounceRef.current )
            debounceRef.current = setTimeout( () =>
            {
                triggerComputation()
            }, 300 )
        }

        checkAndCompute()
        pollRef.current = setInterval( checkAndCompute, 3000 )
        const initTimer = setTimeout( checkAndCompute, 1000 )

        return () =>
        {
            if ( pollRef.current ) clearInterval( pollRef.current )
            if ( debounceRef.current ) clearTimeout( debounceRef.current )
            clearTimeout( initTimer )
        }
    }, [ activeSymbol, inspectionSource, baseOptsFingerprint, effectiveExpiry, range, step ] )

    const triggerComputation = () =>
    {
        const h = historyRef.current
        if ( !h || h.length === 0 || !activeSymbol ) return

        // 🚀 CACHE CHECK: Have we already computed this exact history array?
        const cacheKey = `${activeSymbol}-${effectiveExpiry}-${range}-${step}-${h.length}`
        if ( GLOBAL_COG_CACHE.has( cacheKey ) )
        {
            // Instantly sync to store without recalculating
            useMarketStore.getState().setCogHistory( activeSymbol, GLOBAL_COG_CACHE.get( cacheKey )! )
            return
        }

        // 🚀 SSOT CHECK: If GlobalHistoryFetcher's COMPUTE_ALL already populated cogHistory,
        // skip the redundant Worker call. Check if store already has data for this symbol.
        const existingCog = useMarketStore.getState().cogHistory?.[activeSymbol]
        if (existingCog && existingCog.length === h.length) {
            // Cache it so future calls are instant
            GLOBAL_COG_CACHE.set(cacheKey, existingCog)
            return
        }

        // Prevent duplicate worker calls
        if ( computingRef.current ) return
        computingRef.current = true

        // 🚀 ZERO-FREEZE: Dispatch ALL math to the Web Worker.
        // The main thread does ZERO sorting, filtering, or mass calculations.
        // The Worker runs computeCogHistory() and returns the final CogRow[] array.
        const displayBaseOpts = ( baseOptsRef.current || [] ) as any[]

        runInWorker( 'COMPUTE_COG_HISTORY', {
            history: h,
            symbol: activeSymbol,
            expiry: effectiveExpiry || '',
            activeSubTab: 'Top CE / PE OI',
            isVolumeMode: false,
            step,
            range,
            baseOpts: displayBaseOpts,
        } ).then( ( result: CogRow[] ) =>
        {
            computingRef.current = false
            if ( !result || !Array.isArray( result ) ) return

            // Deduplicate and sort (lightweight, ~0.5ms for 300 rows)
            const map = new Map<string, CogRow>()
            for ( const r of result ) map.set( r.time, r )
            const finalRows = Array.from( map.values() ).sort( ( a, b ) => b.time.localeCompare( a.time ) )

            // Cache and publish
            GLOBAL_COG_CACHE.set( cacheKey, finalRows )
            useMarketStore.getState().setCogHistory( activeSymbol, finalRows )
        } ).catch( ( err: any ) =>
        {
            computingRef.current = false
            console.error( '[useCogEngine] Worker computation failed:', err )
        } )
    }
}